import { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Print } from "@mui/icons-material";
import ApiClient from "../../apis/Api-client";
import { END_POINT } from "../../utils/End-points";
import { Helper } from "../../utils/Helpers";
import { format } from "date-fns";
import { printRefundReceipt } from "./refundReceiptPrint";
import { errorToster } from "../../shared/toster/Toster";

const { getLocalTimeFromUtc, roundOff } = new Helper();

const RefundHistory = ({ bookingId, timezone }) => {
  const { palette } = useTheme();
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printingKey, setPrintingKey] = useState(null);

  useEffect(() => {
    if (bookingId) fetchRefunds();
  }, [bookingId]);

  const fetchRefunds = async () => {
    setLoading(true);
    try {
      const { api } = ApiClient();
      const res = await api.get(END_POINT.REFUND.HISTORY(bookingId));
      if (res?.data?.data) {
        setRefunds(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch refund history", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!refunds.length) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        sx={{
          fontSize: "clamp(14px, 1.05vw, 17px)",
          fontWeight: 600,
          mb: 1,
          background: palette.text?.gradient || "#F7A86E",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Refund History
      </Typography>
      <TableContainer sx={{ width: 1, overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: palette.tab?.gradient || "linear-gradient(90deg, #F7A86E 0%, #F8C56D 100%)", textWrap: "nowrap" }}>
              {["#", "Date", "Amount", "Method", "Reason", "Status", ""].map(
                (label, index) => (
                  <TableCell
                    key={index}
                    align={index === 0 || index === 6 ? "center" : "left"}
                    sx={{
                      fontWeight: 600,
                      color: palette.primary?.contrastText || "#FFFFFF",
                      fontSize: "clamp(12px, 0.9vw, 15px)",
                      py: { xs: "2vw", sm: "inherit" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {refunds.map((refund, index) => (
              <TableRow key={refund.refund_key}>
                <TableCell align="center" sx={{ fontSize: "clamp(12px, 0.9vw, 15px)", py: 0.5 }}>
                  {index + 1}
                </TableCell>
                <TableCell sx={{ fontSize: "clamp(12px, 0.9vw, 15px)", whiteSpace: "nowrap", py: 0.5 }}>
                  {refund.refund_date
                    ? format(
                        new Date(
                          getLocalTimeFromUtc(refund.refund_date, timezone)?.dateTimeString || refund.refund_date
                        ),
                        "dd-MM-yyyy"
                      )
                    : "-"}
                </TableCell>
                <TableCell sx={{ fontSize: "clamp(12px, 0.9vw, 15px)", fontWeight: 500, py: 0.5 }}>
                  {roundOff(refund.refund_amount)}
                </TableCell>
                <TableCell sx={{ fontSize: "clamp(12px, 0.9vw, 15px)", py: 0.5 }}>
                  {refund.refund_payment_type || "-"}
                </TableCell>
                <TableCell sx={{ fontSize: "clamp(12px, 0.9vw, 15px)", py: 0.5 }}>
                  {refund.refund_reason?.replace(/_/g, " ") || "-"}
                </TableCell>
                <TableCell sx={{ fontSize: "clamp(12px, 0.9vw, 15px)", py: 0.5 }}>
                  {refund.refund_status}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5 }}>
                  {refund.refund_status === "COMPLETED" && (
                    <Tooltip title="Print Receipt">
                      <IconButton
                        size="small"
                        disabled={printingKey === refund.refund_key}
                        onClick={async () => {
                          setPrintingKey(refund.refund_key);
                          const { api } = ApiClient();
                          await printRefundReceipt(api, refund.refund_key, errorToster);
                          setPrintingKey(null);
                        }}
                      >
                        {printingKey === refund.refund_key ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Print fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

    </Box>
  );
};

export default RefundHistory;
